import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { SlidersInterface } from "../../constants/Landing";
import CarouselSkeleton from "./skeleton/CarouselSkeleton";

const Carousel = ({
  carouselData,
  isCarouselDataLoading,
}: {
  carouselData: SlidersInterface[] | undefined;
  isCarouselDataLoading: boolean;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = () => {
    if (carouselData) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselData.length);
    }
  };

  const prevSlide = () => {
    if (carouselData) {
      setCurrentIndex(
        (prevIndex) =>
          (prevIndex - 1 + carouselData.length) % carouselData.length
      );
    }
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <Box w={"full"}>
      {isCarouselDataLoading ? (
        <CarouselSkeleton />
      ) : (
        <Box
          className="relative-box"
          position="relative"
          overflow="hidden"
          rounded={{
            base: "12px",
            sm: "14px",
            md: "14px",
            lg: "16px",
            xl: "16px",
            "2xl": "18px",
            "3xl": "20px",
            "4xl": "24px",
            "5xl": "32px",
          }}
          h={"auto"}
          background={"black"}
          mx={"auto"}
        >
          <Flex
            transition="transform 0.5s ease-in-out"
            transform={`translateX(-${currentIndex * 100}%)`}
            as="div"
            align={"stretch"}
            flexShrink={0}
            h={"full"}
          >
            {carouselData &&
              carouselData.map((slide, index) => (
                <Box as="div" key={index} minW="100%" position="relative">
                  <Box
                    as={"div"}
                    h={"full"}
                    alignContent={"center"}
                    bgImage={
                      slide?.picture
                        ? `url(${process.env.REACT_APP_MEDIA_URL}${slide?.picture})`
                        : ""
                    }
                    bgGradient={
                      !slide?.picture
                        ? `linear(to-b,  #9B2C2C, #F56565)`
                        : undefined
                    }
                    backgroundSize="cover"
                    backgroundPosition="center"
                    position={"relative"}
                  >
                    <Flex
                      className="flex-wrap-content"
                      as={"div"}
                      w={"calc(100% - 100px)"}
                      mx={"auto"}
                      px={[4, null, 6]}
                      flexDirection={{ base: "column-reverse", xl: "row" }}
                      h={slide.side_image ? undefined : "full"}
                      gap={4}
                      py={
                        slide.side_image
                          ? [4, 12, null, null, 6]
                          : [24, null, 28]
                      }
                      overflow={"hidden"}
                    >
                      <VStack
                        className="left-container"
                        gap={{ base: "20px", xl: "40px" }}
                        color={slide.text_color ? slide.text_color : "white"}
                        justifyContent={slide.side_image ? "center" : "end"}
                        alignItems={"start"}
                        overflowY={"hidden"}
                      >
                        <Box
                          p={4}
                          bg={
                            slide.overlay === 1 ? "blackAlpha.500" : undefined
                          }
                          flex={"none"}
                          maxW={{ xl: "750px" }}
                          rounded={"md"}
                        >
                          <Text
                            as={"h2"}
                            textStyle={"h-title"}
                            lineHeight={1.2}
                            maxW={"680px"}
                          >
                            {slide?.title}
                          </Text>

                          <Text as={"p"} maxW={"90%"} textStyle={"h-subtitle"}>
                            {slide?.subtitle}
                          </Text>
                        </Box>

                        {slide?.use_button && (
                          <Button
                            as={Link}
                            to={slide.link}
                            background={slide?.button_color || "#FFA500"}
                            variant="solid"
                            fontWeight={"bold"}
                            fontSize={{ base: "12px", xl: "16px" }}
                            color={slide?.text_button_color || "black"}
                          >
                            {slide?.text_button || "Gabung Sekarang"}
                          </Button>
                        )}
                      </VStack>

                      {slide?.side_image && (
                        <Image
                          className="ratio-1-1"
                          src={
                            slide?.side_image
                              ? `${process.env.REACT_APP_MEDIA_URL}${slide?.side_image}`
                              : ""
                          }
                          h={"auto"}
                          maxH={"full"}
                          w={"full"}
                          mx={"auto"}
                          alt="Image Carousel"
                          borderRadius={["lg", null, "xl"]}
                          maxW={[
                            "180px",
                            "200px",
                            "240px",
                            "280px",
                            "320px",
                            "400px",
                          ]}
                          objectFit={"cover"}
                        />
                      )}
                    </Flex>
                  </Box>
                </Box>
              ))}
          </Flex>

          {/* Arrows Navigation */}
          <IconButton
            w={"20px"}
            aria-label="Previous Slide"
            icon={<ChevronLeftIcon boxSize={6} />}
            position="absolute"
            top="50%"
            left={4}
            transform="translateY(-50%)"
            bg="blackAlpha.600"
            color="white"
            _hover={{ bg: "blackAlpha.800" }}
            onClick={prevSlide}
          />

          <IconButton
            w={"20px"}
            aria-label="Next Slide"
            icon={<ChevronRightIcon boxSize={6} />}
            position="absolute"
            top="50%"
            right={4}
            transform="translateY(-50%)"
            bg="blackAlpha.600"
            color="white"
            _hover={{ bg: "blackAlpha.800" }}
            onClick={nextSlide}
          />

          {/* Dots Navigation */}
          <HStack
            position="absolute"
            bottom="4"
            left="50%"
            transform="translateX(-50%)"
            spacing={2}
          >
            {carouselData &&
              carouselData.map((_, index) => (
                <Box
                  key={index}
                  as="button"
                  onClick={() => goToSlide(index)}
                  width={4}
                  height="4"
                  bg={currentIndex === index ? "#cf5f00" : "gray.400"}
                  borderRadius="xl"
                />
              ))}
          </HStack>
        </Box>
      )}
    </Box>
  );
};

export default Carousel;
